import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/yanre/Logo";
import { Reveal } from "@/components/yanre/Reveal";
import { useLocale } from "@/i18n/LocaleContext";
import { lpPath, PROD_BASE, localeTag, OYG_SEO_LOCALES } from "@/i18n";
import { openCookiePreferences } from "@/lib/consent";

// Cookie Policy — factual page about this site's consent system only.
// No invented legal/company data: covers the consent store, categories,
// the single GTM container with Consent Mode v2, and existing contact points.
const Section = ({ title, children, id, testId }) => (
  <section id={id} className="mt-12" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="h-px w-6 bg-signal" />
      <h2 className="font-display text-xl font-bold uppercase tracking-wide text-white sm:text-2xl">{title}</h2>
    </div>
    <div className="mt-4 space-y-4 text-sm font-light leading-relaxed text-titanium/70 sm:text-base">{children}</div>
  </section>
);

export default function CookiePolicy() {
  const { locale, t } = useLocale();

  useEffect(() => {
    document.title = `${t("cp.title", "Cookie Policy")} | YANRE FITNESS EUROPE`;
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", t("cp.intro", "How YANRE FITNESS EUROPE uses cookies and how you can control them."));
    setMeta("robots", "index, follow");

    // Canonical + hreflang — mirrors the sitemap.xml annotations for this family.
    const policyPathFor = (l) => (l === "en" ? "/cookie-policy" : `/${l}/cookie-policy`);
    document.head.querySelectorAll('link[data-seo="canonical"], link[data-seo="hreflang"]').forEach((e) => e.remove());
    const addLink = (rel, hreflang, href) => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      if (hreflang) el.setAttribute("hreflang", hreflang);
      el.setAttribute("href", href);
      el.dataset.seo = hreflang ? "hreflang" : "canonical";
      document.head.appendChild(el);
    };
    addLink("canonical", null, `${PROD_BASE}${policyPathFor(locale)}`);
    OYG_SEO_LOCALES.forEach((l) => addLink("alternate", localeTag(l), `${PROD_BASE}${policyPathFor(l)}`));
    addLink("alternate", "x-default", `${PROD_BASE}/cookie-policy`);
  }, [locale, t]);

  const home = lpPath(locale);

  return (
    <main className="min-h-screen bg-carbon-950 font-sans text-titanium" data-testid="cookie-policy-page">
      <header className="border-b border-carbon-700">
        <div className="container-edge flex items-center justify-between py-6">
          <Link to={home} aria-label="YANRE FITNESS EUROPE" data-testid="cookie-policy-logo">
            <Logo />
          </Link>
          <Link
            to={home}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/60 transition-colors hover:text-white"
            data-testid="cookie-policy-back"
          >
            <ArrowLeft className="h-3.5 w-3.5 rtl:-scale-x-100" />
            {t("cp.back", "Back to Home")}
          </Link>
        </div>
      </header>

      <article className="container-edge max-w-3xl py-16 lg:py-24">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-signal" />
            <span className="overline">YANRE FITNESS EUROPE</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-6xl" data-testid="cookie-policy-title">
            {t("cp.title", "Cookie Policy")}
          </h1>
        </Reveal>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/40" data-testid="cookie-policy-updated">
          {t("cp.updated", "Last updated: July 2026")}
        </p>
        <p className="mt-8 text-base font-light leading-relaxed text-titanium/80 sm:text-lg" data-testid="cookie-policy-intro">
          {t("cp.intro", "This page explains how YANRE FITNESS EUROPE uses cookies and similar technologies on this website, and how you can control them.")}
        </p>

        <Section title={t("cp.s1t", "What we store")} testId="cookie-policy-storage">
          <p>
            {t(
              "cp.s1b",
              "When you make a consent choice, we store only that choice (necessary, analytics, advertising) in your browser's local storage. It contains no personal information and is used solely to remember your preference between visits."
            )}
          </p>
        </Section>

        <Section title={t("cp.s2t", "Cookie categories")} testId="cookie-policy-categories">
          <div className="space-y-4">
            <div className="border border-carbon-700 bg-carbon-900 p-5">
              <div className="flex items-center justify-between gap-4">
                <p className="font-display text-base font-bold uppercase tracking-wide text-white">{t("ck.necessary", "Necessary")}</p>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal">{t("ck.alwaysOn", "Always active")}</span>
              </div>
              <p className="mt-2">{t("ck.necessaryDesc", "Required for the website to function. Always active.")}</p>
            </div>
            <div className="border border-carbon-700 bg-carbon-900 p-5">
              <p className="font-display text-base font-bold uppercase tracking-wide text-white">{t("ck.analytics", "Analytics")}</p>
              <p className="mt-2">{t("ck.analyticsDesc", "Anonymous usage statistics that help us improve this website.")}</p>
            </div>
            <div className="border border-carbon-700 bg-carbon-900 p-5">
              <p className="font-display text-base font-bold uppercase tracking-wide text-white">{t("ck.advertising", "Advertising")}</p>
              <p className="mt-2">{t("ck.advertisingDesc", "Used to measure campaigns and deliver relevant advertising.")}</p>
            </div>
          </div>
        </Section>

        <Section title={t("cp.s3t", "Google Tag Manager & Consent Mode")} testId="cookie-policy-gtm">
          <p>
            {t(
              "cp.s3b",
              "This website loads one Google Tag Manager container with Google Consent Mode v2. All optional storage (analytics and advertising) is set to \"denied\" by default and is only enabled after your explicit consent. No analytics or advertising tags are installed directly on this website."
            )}
          </p>
        </Section>

        <Section title={t("cp.s4t", "Managing your consent")} testId="cookie-policy-manage">
          <p>
            {t(
              "cp.s4b",
              "You can change or withdraw your consent at any time via the \"Cookie Preferences\" link in the website footer. Withdrawal takes effect immediately."
            )}
          </p>
          <p>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="btn-ghost border-titanium/30 text-titanium hover:border-signal hover:text-white"
              data-testid="cookie-policy-open-preferences"
            >
              {t("ck.prefsTitle", "Cookie Preferences")}
            </button>
          </p>
        </Section>

        <Section title={t("cp.s5t", "Contact")} testId="cookie-policy-contact">
          <p>
            <a href="mailto:yanre-eu@yanrefitness.com" className="text-white underline-offset-4 hover:underline" data-testid="cookie-policy-email">
              yanre-eu@yanrefitness.com
            </a>
          </p>
          <p>
            <a href="tel:+351910619033" className="text-white underline-offset-4 hover:underline" data-testid="cookie-policy-phone">
              +351 910 619 033
            </a>
          </p>
        </Section>

        <Section title={t("cp.privacyT", "Privacy")} id="privacy" testId="cookie-policy-privacy">
          <p>
            {t(
              "cp.privacyB",
              "Our full Privacy Policy is being finalized. For any privacy question or request regarding your personal data, contact us at the email above."
            )}
          </p>
        </Section>
      </article>

      <footer className="border-t border-carbon-700">
        <div className="container-edge flex flex-col gap-3 py-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">
            © {new Date().getFullYear()} YANRE FITNESS EUROPE
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/30">Factory Direct · Since 1997</p>
        </div>
      </footer>
    </main>
  );
}
